import { Create } from "@refinedev/mui";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  TextField,
} from "@mui/material";
import { useForm } from "@refinedev/react-hook-form";
import "react-image-crop/dist/ReactCrop.css";

import { IResourceComponentsProps, useInvalidate, useShow } from "@refinedev/core";
import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import CancelIcon from "@mui/icons-material/Cancel";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { NumericFormatCustom } from "components/inputs/custom-numeric-input";
import React from "react";
import { primary } from "style/color";
import { CategoryInfiniteSelect } from "components/inputs/paginated-drop-down";
import { TagsInfiniteSelect } from "components/inputs/multiple-choice-paginated-drop-down";
import { ImageUploadWithCrop } from "components/form/ImageUploadWithCrop";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

export const GrantVestmentCreate: React.FC<IResourceComponentsProps> = () => {
  const invalidate = useInvalidate();
  const [searchParams] = useSearchParams();
  const duplicateFromId = searchParams.get("duplicate_from");
  const [aboutPreview, setAboutPreview] = useState<string>("");

  const { queryResult } = useShow({
    resource: "grant-vestments",
    id: duplicateFromId || "",
    queryOptions: {
      enabled: !!duplicateFromId,
    },
  });

  const {
    refineCore: { formLoading, onFinish },
    register,
    handleSubmit,
    setValue,
    getValues,
    clearErrors,
    formState: { errors },
    watch,
  } = useForm({
    refineCoreProps: {
      redirect: "show",
      errorNotification: (error): any => {
        return {
          message:
            error?.message && "Oops! something went wrong, please try later.",
          type: "error",
        };
      },
      onMutationSuccess: () => {
        invalidate({
          resource: "grant-vestments",
          invalidates: ["list"],
        });
      },
    },
    warnWhenUnsavedChanges: true,
  });

  interface LinkList {
    title: string;
    url: string;
  }

  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [dataRoom, setDataRoom] = useState<LinkList[]>([
    { title: "", url: "" },
  ]);
  const [updates, setUpdates] = useState<{ title: string; url: string }[]>([
    { title: "", url: "" },
  ]);

  const grantVestmentData = queryResult?.data?.data;

  // Create modified data with overridden values for form display
  const modifiedGrantVestmentData = useMemo(() => {
    if (!grantVestmentData) return null;
    const originalName = grantVestmentData.name || "";
    const nameWithCopy = duplicateFromId && originalName
      ? `${originalName} (Copy)`
      : originalName;
    return {
      ...grantVestmentData,
      // Name: add "(Copy)" suffix when duplicating
      name: nameWithCopy,
      // Deadline: default to 12/31 of the current year
      deadline: `${new Date().getFullYear()}-12-31`,
      // Goal amount: 100,000
      goalAmount: 100000,
      // Per annum fee: -1
      perAnnumFee: -1,
      // All or Nothing: FALSE
      allOrNothing: false,
      // Status: DRAFT
      status: "DRAFT",
      // Updates: empty array
      updates: [],
    } as any;
  }, [grantVestmentData, duplicateFromId]);

  React.useEffect(() => {
    register("categoryId", {
      required: "Category is required",
    });
  }, [register]);

  React.useEffect(() => {
    register("tagIds[]");
  }, [register]);

  // Pre-populate form when duplicating
  useEffect(() => {
    if (modifiedGrantVestmentData) {
      // Set all form values from modified data
      setValue("name", modifiedGrantVestmentData?.name || "");
      setValue("description", modifiedGrantVestmentData?.description || "");
      setValue("about", modifiedGrantVestmentData?.about || "");
      setValue("projectBy", modifiedGrantVestmentData?.projectBy || "");
      setValue("categoryId", modifiedGrantVestmentData?.category?.categoryId || "");
      setValue("imageUrl", modifiedGrantVestmentData?.imageUrl || "");
      setValue("deadline", modifiedGrantVestmentData?.deadline?.substring(0, 10) || "");
      setValue("goalAmount", modifiedGrantVestmentData?.goalAmount || 100000);
      setValue("perAnnumFee", modifiedGrantVestmentData?.perAnnumFee ?? -1);
      setValue("allOrNothing", modifiedGrantVestmentData?.allOrNothing?.toString() || "false");
      setValue("status", modifiedGrantVestmentData?.status || "DRAFT");
      setValue("nonDAF", modifiedGrantVestmentData?.nonDAF || 0);
      setValue("showGrantvestment", grantVestmentData?.showGrantvestment?.toString() || "true");

      // Tags: keep exactly the same from original data
      let currentTags: string[] = [];
      if (grantVestmentData?.tags && grantVestmentData?.tags.length > 0) {
        grantVestmentData?.tags?.map((tag: any) =>
          currentTags.push(tag.tagId.toString())
        );
        setSelectedTags(currentTags);
      }

      // Data room: keep exactly the same from original data
      if (grantVestmentData?.dataRoom && grantVestmentData?.dataRoom.length > 0) {
        let dataRoomTab: { title: string; url: string }[] = [];
        grantVestmentData?.dataRoom.map(
          (dataRoom: { title: string; url: string }) =>
            dataRoomTab.push({ title: dataRoom.title, url: dataRoom.url })
        );
        setDataRoom(dataRoomTab);
      } else {
        setDataRoom([{ title: "", url: "" }]);
      }

      // Updates: leave blank (empty array)
      setUpdates([{ title: "", url: "" }]);
    }
  }, [
    modifiedGrantVestmentData,
    grantVestmentData,
    setValue,
  ]);

  watch("image");

  const handleDataRoom = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const { name, value } = event.target;
    let onChangeValue = [...dataRoom];
    (onChangeValue[index] as any)[name] = value;
    setDataRoom(onChangeValue);
  };

  const handleUpdates = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const { name, value } = event.target;
    let onChangeValue = [...updates];
    (onChangeValue[index] as any)[name] = value;
    setUpdates(onChangeValue);
  };
  // Show loading state when fetching duplicate data
  if (duplicateFromId && queryResult?.status === "loading") {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 5 }}>
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Create
      isLoading={formLoading}
      saveButtonProps={{
        disabled: formLoading || Object.keys(errors).length !== 0,
        onClick: (e: React.BaseSyntheticEvent) => {
          setValue("dataRoom[]", dataRoom);
          setValue("updates[]", updates);
          setValue("tagIds[]", selectedTags);
          const goalAmount = getValues("goalAmount").toString();
          const value = parseFloat(goalAmount.replaceAll(",", ""));
          setValue("goalAmount", value);
          const nonDAF = parseFloat(
            getValues("nonDAF").toString().replaceAll(",", "")
          );
          setValue("nonDAF", nonDAF);
          const perAnnumFee = parseFloat(
            getValues("perAnnumFee").toString().replaceAll(",", "")
          );
          setValue("perAnnumFee", perAnnumFee);

          handleSubmit(onFinish, () => false)(e);
        },
      }}
    >
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <InputLabel style={{ fontSize: "12px" }}>Name</InputLabel>
        <TextField
          {...register("name", {
            required: "This GrantVestment name is required",
          })}
          error={!!(errors as any)?.name}
          helperText={(errors as any)?.name?.message}
          margin="normal"
          fullWidth
          style={{ marginTop: "1px" }}
          InputLabelProps={{ hidden: true }}
          type="text"
          name="name"
          defaultValue={modifiedGrantVestmentData?.name || ""}
        />

        <InputLabel style={{ fontSize: "12px" }}>Description</InputLabel>
        <TextField
          {...register("description", {
            required: "This GrantVestment description section is required",
          })}
          error={!!(errors as any)?.description}
          helperText={(errors as any)?.description?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ hidden: true }}
          style={{ marginTop: "1px" }}
          type="textarea"
          minRows={3}
          rows={3}
          maxRows={50}
          multiline
          name="description"
          defaultValue={modifiedGrantVestmentData?.description || ""}
        />

        <div data-color-mode="light">
          <InputLabel style={{ fontSize: "12px", marginBottom: "3px" }}>
            About
          </InputLabel>
          <div
            style={{
              border: "2px solid ",
              borderRadius: "5px",
              borderColor: (errors as any)?.about ? "#d3302f" : "transparent",
            }}
          >
            <Grid container spacing={2}>
              <Grid xs={6} md={6}>
                <div
                  style={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "5px 0 0 5px",
                    height: "350px",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    position: "relative",
                    marginLeft: "17px",
                    marginTop: "14px",
                  }}
                >
                  <div style={{ flex: 1, overflow: "hidden" }}>
                    <div style={{ height: "100%", overflow: "auto" }}>
                      <CKEditor
                        editor={ClassicEditor}
                        data={getValues("about") || ""}
                        config={{
                          placeholder: "Please enter about",
                          removePlugins: ["PoweredBy"],
                          toolbar: [
                            "heading",
                            "|",
                            "bold",
                            "italic",
                            "|",
                            "bulletedList",
                            "numberedList",
                            "|",
                            "blockQuote",
                            "insertTable",
                            "|",
                            "undo",
                            "redo",
                          ],
                        }}
                        onChange={(_, editor) => {
                          const data = editor.getData();
                          setAboutPreview(data);
                          clearErrors("about");
                          setValue("about", data, {
                            shouldValidate: true,
                            shouldDirty: true,
                          });
                        }}
                        onBlur={(_, editor) => {
                          const data = editor.getData();
                          setValue("about", data, {
                            shouldValidate: true,
                          });
                        }}
                      />
                    </div>
                  </div>
                </div>
                <InputLabel
                  style={{
                    fontSize: "12px",
                    margin: "3px",
                    color: "#d3302f",
                    paddingLeft: "10px",
                  }}
                >
                  {!!(errors as any)?.about && (errors as any)?.about?.message}
                </InputLabel>
              </Grid>

              {/* Preview */}
              <Grid xs={6} md={6}>
                <div
                  style={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "1px",
                    height: "350px",
                    overflow: "auto",
                    padding: "16px",
                    backgroundColor: "#fafafa",
                    marginTop: "14px",
                    fontFamily: 'Arial, "Helvetica Neue", Helvetica, sans-serif',
                  }}
                >
                  <InputLabel
                    style={{
                      fontSize: "12px",
                      marginBottom: "8px",
                      fontWeight: "bold",
                      fontFamily: 'Arial, "Helvetica Neue", Helvetica, sans-serif',
                    }}
                  >
                    Preview
                  </InputLabel>

                  <div
                    className="ck-content"
                    dangerouslySetInnerHTML={{
                      __html: aboutPreview || getValues("about") || "",
                    }}
                    style={{
                      lineHeight: 1.6,
                      color: "#333",
                    }}
                  />
                </div>
              </Grid>
            </Grid>
          </div>
          <InputLabel
            style={{
              fontSize: "12px",
              margin: "3px",
              color: "#d3302f",
              paddingLeft: "10px",
            }}
          >
            {!!(errors as any)?.about && (errors as any)?.about?.message}
          </InputLabel>
        </div>

        <InputLabel style={{ fontSize: "12px" }}>Project By</InputLabel>
        <TextField
          {...register("projectBy", {
            required: "This GrantVestment project By is required",
          })}
          error={!!(errors as any)?.projectBy}
          helperText={(errors as any)?.projectBy?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ hidden: true }}
          style={{ marginTop: "1px" }}
          type="text"
          name="projectBy"
          defaultValue={modifiedGrantVestmentData?.projectBy || ""}
        />

        <InputLabel style={{ fontSize: "12px" }}>Deadline</InputLabel>
        <TextField
          {...register("deadline", {
            required: "This GrantVestment deadline is required",
          })}
          error={!!(errors as any)?.deadline}
          helperText={(errors as any)?.deadline?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ hidden: true }}
          type="date"
          name="deadline"
          style={{ marginTop: "1px" }}
          inputProps={{
            min: new Date().toISOString().substring(0, 10),
          }}
          defaultValue={modifiedGrantVestmentData?.deadline?.substring(0, 10) || ""}
        />

        <InputLabel style={{ fontSize: "12px" }}>Goal Amount</InputLabel>
        <TextField
          {...register("goalAmount", {
            required: "This GrantVestment goal amount is required",
          })}
          error={!!(errors as any)?.goalAmount}
          helperText={(errors as any)?.goalAmount?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ hidden: true }}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
            inputComponent: NumericFormatCustom as any,
          }}
          defaultValue={modifiedGrantVestmentData?.goalAmount || ""}
          name="goalAmount"
          style={{ marginTop: "1px" }}
        />

        <InputLabel style={{ fontSize: "12px" }}>Non DAF</InputLabel>
        <TextField
          {...register("nonDAF", {
            required: "This non Daf is required",
          })}
          error={!!(errors as any)?.nonDAF}
          helperText={(errors as any)?.nonDAF?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ hidden: true }}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
            inputComponent: NumericFormatCustom as any,
          }}
          value={getValues("nonDAF") ?? 0}
          name="nonDAF"
          style={{ marginTop: "1px" }}
        />

        <InputLabel style={{ fontSize: "12px" }}>UpFront Fee</InputLabel>
        <TextField
          margin="normal"
          fullWidth
          InputLabelProps={{ hidden: true }}
          InputProps={{
            inputComponent: NumericFormatCustom as any,
          }}
          name="upFrontFee"
          value={0}
          style={{ marginTop: "1px" }}
          disabled
        />

        <InputLabel style={{ fontSize: "12px" }}>
          UpFront Percentage Fee
        </InputLabel>
        <TextField
          margin="normal"
          fullWidth
          InputLabelProps={{ hidden: true }}
          InputProps={{
            inputComponent: NumericFormatCustom as any,
          }}
          name="upFrontPercentageFee"
          value={0}
          style={{ marginTop: "1px" }}
          disabled
        />

        <InputLabel style={{ fontSize: "12px" }}>Per Annum Fee</InputLabel>
        <TextField
          {...register("perAnnumFee", {
            required: "This GrantVestment per annum fee required!",
          })}
          error={!!(errors as any)?.perAnnumFee}
          helperText={(errors as any)?.perAnnumFee?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ hidden: true }}
          InputProps={{
            inputComponent: NumericFormatCustom as any,
          }}
          defaultValue={modifiedGrantVestmentData?.perAnnumFee ?? -1}
          name="perAnnumFee"
          style={{ marginTop: "1px" }}
        />

        <InputLabel style={{ fontSize: "12px" }}>All Or Nothing</InputLabel>
        <TextField
          {...register("allOrNothing", {
            required: "This GrantVestment all Or Nothing is required",
          })}
          error={!!(errors as any)?.allOrNothing}
          helperText={(errors as any)?.allOrNothing?.message}
          select
          margin="normal"
          fullWidth
          InputLabelProps={{ hidden: true }}
          defaultValue={modifiedGrantVestmentData?.allOrNothing?.toString() || "false"}
          name="allOrNothing"
          style={{ marginTop: "1px" }}
        >
          {[
            { value: "true", label: "True" },
            { value: "false", label: "False" },
          ].map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <InputLabel style={{ fontSize: "12px" }}>
          Display on Public Site
        </InputLabel>
        <TextField
          {...register("showGrantvestment", {
            required: "This GrantVestment Display on Public Site is required",
          })}
          error={!!(errors as any)?.showGrantvestment}
          helperText={(errors as any)?.showGrantvestment?.message}
          select
          margin="normal"
          fullWidth
          InputLabelProps={{ hidden: true }}
          defaultValue={modifiedGrantVestmentData?.showGrantvestment?.toString() || grantVestmentData?.showGrantvestment?.toString() || "true"}
          name="showGrantvestment"
          style={{ marginTop: "1px" }}
        >
          {[
            { value: "true", label: "True" },
            { value: "false", label: "False" },
          ].map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <InputLabel style={{ fontSize: "12px" }}>Status</InputLabel>
        <TextField
          {...register("status", {
            required: "This GrantVestment status is required",
          })}
          error={!!(errors as any)?.status}
          helperText={(errors as any)?.status?.message}
          select
          margin="normal"
          fullWidth
          InputLabelProps={{ hidden: true }}
          defaultValue={modifiedGrantVestmentData?.status || "DRAFT"}
          name="status"
          style={{ marginTop: "1px" }}
        >
          {[
            { value: "RAISING", label: "Raising" },
            { value: "CLOSED", label: "Closed" },
            { value: "DRAFT", label: "Draft" },
            { value: "PROMOTING", label: "Promoting" },
            { value: "CANCELED", label: "Canceled" },
            { value: "EXITED", label: "Exited" },
          ].map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <InputLabel style={{ fontSize: "12px" }}>Category</InputLabel>
        <CategoryInfiniteSelect
          value={getValues("categoryId")}
          onChange={(categoryId) => {
            setValue("categoryId", categoryId, {
              shouldValidate: true,
              shouldDirty: true,
            });
            clearErrors("categoryId");
          }}
          error={!!(errors as any)?.categoryId}
          helperText={(errors as any)?.categoryId?.message}
          initialCategory={grantVestmentData?.category}
        />
        <InputLabel style={{ fontSize: "12px" }}>Image</InputLabel>
        <ImageUploadWithCrop
          onImageUpload={(imageUrl) => {
            setValue("imageUrl", imageUrl);
          }}
          existingImageUrl={getValues("imageUrl")}
          minWidth={900}
          minHeight={600}
          recommendedWidth={1200}
          recommendedHeight={800}
          minCropWidth={600}
          minCropHeight={400}
        />

        <InputLabel style={{ fontSize: "12px" }}>Tags</InputLabel>
        <TagsInfiniteSelect
          value={selectedTags}
          onChange={(tagIds) => {
            setSelectedTags(tagIds);
            setValue("tagIds[]", tagIds);
          }}
          error={!!(errors as any)?.["tagIds[]"]}
          helperText={(errors as any)?.["tagIds[]"]?.message}
          initialTags={grantVestmentData?.tags}
        />

        <InputLabel sx={{ mt: 2, color: primary[900] }}>Data room</InputLabel>
        {dataRoom.map((item, index) => (
          <Grid container key={index}>
            <Grid xs={12} sm={12} md={5} paddingRight={{ md: 2 }}>
              <TextField
                id="dataRoom-title"
                value={item.title}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="Title"
                name="title"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleDataRoom(event, index)
                }
              />
            </Grid>
            <Grid xs={12} sm={12} md={5} paddingRight={{ md: 2 }}>
              <TextField
                id="dataRoom-url"
                value={item.url}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="URL"
                name="url"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleDataRoom(event, index)
                }
              />
            </Grid>
            <Grid xs={12} sm={12} md={2} paddingRight={{ md: 2 }}>
              {dataRoom.length > 1 && (
                <Button
                  sx={{ my: 3 }}
                  onClick={(e) => {
                    e.preventDefault();
                    const newArray = [...dataRoom];
                    newArray.splice(index, 1);
                    setDataRoom(newArray);
                  }}
                >
                  <CancelIcon />
                </Button>
              )}
              {index === dataRoom.length - 1 && (
                <Button
                  sx={{ my: 3 }}
                  onClick={() =>
                    setDataRoom([...dataRoom, { title: "", url: "" }])
                  }
                >
                  <AddCircleOutlineIcon />
                </Button>
              )}
            </Grid>
          </Grid>
        ))}

        <InputLabel sx={{ mt: 2, color: primary[900] }}>Updates</InputLabel>
        {updates.map((item, index) => (
          <Grid container key={index}>
            <Grid xs={12} sm={12} md={5} paddingRight={{ md: 2 }}>
              <TextField
                id="update-title"
                value={item.title}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="Title"
                name="title"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleUpdates(event, index)
                }
              />
            </Grid>
            <Grid xs={12} sm={12} md={5} paddingRight={{ md: 2 }}>
              <TextField
                id="update-url"
                value={item.url}
                margin="normal"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="text"
                label="URL"
                name="url"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleUpdates(event, index)
                }
              />
            </Grid>
            <Grid xs={12} sm={12} md={2} paddingRight={{ md: 2 }}>
              {updates.length > 1 && (
                <Button
                  sx={{ my: 3 }}
                  onClick={(e) => {
                    e.preventDefault();
                    const newArray = [...updates];
                    newArray.splice(index, 1);
                    setUpdates(newArray);
                  }}
                >
                  <CancelIcon />
                </Button>
              )}
              {index === updates.length - 1 && (
                <Button
                  sx={{ my: 3 }}
                  onClick={() =>
                    setUpdates([...updates, { title: "", url: "" }])
                  }
                >
                  <AddCircleOutlineIcon />
                </Button>
              )}
            </Grid>
          </Grid>
        ))}
      </Box>
    </Create>
  );
};
